import { useCallback, useEffect, useRef, useState } from 'react';
import { Storage, type StorageBackend } from '../lib/storage';
import {
  clubSession,
  memberSession,
  signOutCustomerSessions,
  type ClubSessionState,
} from '../lib/auth';
import {
  selectCustomerSurface,
  selectPlayerClub,
  selectedPlayerClub,
  type PlayerClub,
} from '../lib/api/customerContext';
import { createOnboardingGrant } from '../lib/auth/onboarding';
import {
  clearInvitationFromLocation,
  invitationTokenFromLocation,
} from '../lib/api/memberInvitationAccept';
import { readLastScope, signInLanding, writeLastScope } from '../lib/auth/lastScope';
import { passkeysSupported, signInWithPasskey } from '../lib/api/passkeys';
import type { DevPersona, DevPersonaResult } from '../lib/api/devPersonas';
import { isStandaloneMobile } from '../lib/nativeBridge';
import { isMemberSurfaceLocation } from './useNavigation';
import {
  DevAllRightsMarker,
  DevPersonaBar,
  LoginScreen,
  MemberInvitationScreen,
  MemberOnboardingScreen,
  PlayerSignInScreen,
  NoScopeScreen,
  type PasskeySignInFailure,
  ScopeSelector,
} from '../components/auth';
import { ImpersonationBanner, useImpersonation } from '../components/platform';
import { ListLoadFailedBanner, NewBuildBanner, ServerUnreachableBanner } from '../components/ui';
import { endImpersonation } from '../lib/api/impersonation';
import { navigateToSurface } from './surface';
import { StoreProvider } from '../state/StoreProvider';
import { Padel24Engine } from '../engine/platformEngine';
import { AppShell } from './AppShell';
import { PaymentReturnNotice } from '../components/booking/PaymentReturnNotice';
import { peekPaymentReturn } from '../lib/paymentReturn';
import { useAccountLocale, useT } from '../i18n';
import { clearRestoreUnreachable, restoreUnreachable } from '../lib/auth/restoreReachability';

// ─────────────────────────────────────────────────────────────────────
// Padel24App — the app entry / auth gate. Renders the login screen until the
// user is authenticated, then mounts the StoreProvider + the authenticated
// AppShell. Split out of the former monolith shell (PR-BK) so the entry/auth
// concern is separate from the app shell itself (src/app/AppShell.tsx).
// ─────────────────────────────────────────────────────────────────────
/**
 * Pure: does this page need the club PORTAL's login screen?
 *
 * The login gates the CLUB portal (admin tooling) on the networked backend
 * (the API); the in-memory backend skips it. It never gates the MEMBER
 * SURFACE, which has its own gate — `memberJoinRequiredFor`, which demands a
 * member or account session — so applying both would ask a signed-in player
 * to prove themselves a second time, to a door that is not theirs.
 *
 * ── «THE MEMBER SURFACE», NOT «STANDALONE» ─────────────────────────
 *
 * The second argument was `isStandaloneMobile()` — the native shell, or
 * `?native=1` — which answers a question about LAYOUT. #751 found that exact
 * confusion in `memberJoinRequiredFor` and corrected it there; this gate was
 * left on the old predicate, so in a plain browser a player who signed in and
 * opened their own club was shown the club portal's login. The native app did
 * not have the problem, because there the two predicates agree. Same fix,
 * same reasoning: the page's SURFACE decides, not its frame.
 */
export function loginRequiredFor(backend: StorageBackend, memberSurface: boolean): boolean {
  if (memberSurface) return false;
  return backend === 'http';
}

/**
 * Does the member surface need a member session before it can load?
 *
 * Only on the API backend — which, since #241, is the only networked one.
 * The member surface was public on the retired Supabase path; on the API
 * there is no public read path by design (#185): every
 * member read goes through `/member/bootstrap`, which is scoped by the session,
 * so without one there is nothing to show.
 *
 * ── «THE MEMBER SURFACE», NOT «STANDALONE» (#751) ──────────────────
 *
 * The second argument used to be `isStandaloneMobile()` — the native shell, or
 * `?native=1` in a URL. That made the gate answer a question about the LAYOUT
 * (full viewport, no phone frame) rather than about which surface the page is,
 * and the two are not the same: `#mobile` in an ordinary browser renders the
 * member app and did NOT need a session, so it loaded with no data source and
 * nothing to show. That is the gap #751 opens with — «a player opening the
 * plain web address gets the club portal's login», and the one reachable route
 * in between was broken rather than absent.
 *
 * It is a strict WIDENING, which is why it is safe: the native shell and
 * `?native=1` both still make `initialViewFromLocation` answer `'mobile'`, so
 * everything that required a session before still does. What is added is the
 * plain-browser `#mobile` case.
 */
export function memberJoinRequiredFor(backend: StorageBackend, memberSurface: boolean): boolean {
  return memberSurface && backend === 'http';
}

/**
 * Is the member surface what this page load IS?
 *
 * Read from the location exactly once per load, like the gate it feeds: the
 * shell does not change under a running page, and re-deciding mid-life would
 * swap the app's data source. A dev-persona switch is the one thing that
 * changes it deliberately, and it says so by calling `setMemberJoinNeeded`.
 */
function memberSurfaceAtLoad(): boolean {
  return isMemberSurfaceLocation();
}

/** The club the member's link names, e.g. `?club=padel24`. */
export function clubSlugFromLocation(search: string = window.location.search): string | null {
  try {
    const slug = new URLSearchParams(search).get('club');
    return slug && slug.trim() ? slug.trim().toLowerCase() : null;
  } catch {
    return null;
  }
}

/**
 * THE SURFACE FOLLOWS THE PERSONA (owner report, 2026-10-20).
 *
 * «Spiller» in the DEV menu adopted the member session and moved the address
 * to `#mobile`, but left the SELECTED surface where it was. An explicit
 * selection is final (`customerContext`), and a sign-in now selects `club`
 * (#974) — so after switching to the player persona the transport still
 * answered «club surface», `playerIdentityMode()` answered `'picked'`, and the
 * player app asked «Hvem er du?» and let anyone be chosen, with a member
 * session in hand that already named the person. The persona's kind decides
 * the surface, both ways, exactly as `enterOrganization` and
 * `goToPersonalHome` do for the real transitions.
 */
export function adoptPersonaSurface(kind: 'club' | 'member'): void {
  selectCustomerSurface(kind === 'member' ? 'member' : 'club');
}

/**
 * Rewrite the address bar for a persona switch WITHOUT a reload — a reload
 * would lose the in-memory session the switch just obtained. `native=1` is
 * what `isStandaloneMobile` and `initialViewFromLocation` read, so setting it
 * here is what turns the page into the member app on the next render.
 */
function rewriteLocationForPersona(kind: 'club' | 'member', clubSlug?: string) {
  adoptPersonaSurface(kind);
  try {
    const url = new URL(window.location.href);
    if (kind === 'member') {
      url.searchParams.set('native', '1');
      if (clubSlug) url.searchParams.set('club', clubSlug);
      url.hash = '#mobile';
    } else {
      url.searchParams.delete('native');
      url.searchParams.delete('club');
      url.hash = '';
    }
    window.history.replaceState({}, '', url.toString());
  } catch {
    /* no history API — the switch still happens in memory */
  }
}

/**
 * Move the ADDRESS to match the surface being entered (#752, #830 review).
 *
 * `rewriteLocationForPersona` above does this for a dev-persona switch; this
 * is the same move for the real one, and it exists because `AppShell` reads
 * `initialViewFromLocation()` on its first mount. Entering an organization
 * from `/#mobile` without touching the hash mounted the shell on the MEMBER
 * view — the player app, with a club transport context behind it.
 *
 * `native` is deliberately NOT set when returning to the member surface: that
 * flag means «the Expo shell, or a browser previewing its layout», and
 * inventing it would hide the header — and with it the only way back.
 */
function rewriteLocationForSurface(surface: 'club' | 'member') {
  try {
    const url = new URL(window.location.href);
    url.hash = surface === 'member' ? '#mobile' : '';
    window.history.replaceState({}, '', url.toString());
  } catch {
    /* no history API — the transition still happens in memory */
  }
}

/**
 * The frame shown while a session is being restored — BOTH gates (#579, #620).
 *
 * The dark ground the shell paints, so a restored session does not flash white on
 * the way in, and a word only once the wait is long enough to need explaining.
 * Shared rather than copied: the member app grew the same gate at #620, and the
 * lesson of #579 was that this frame is a diagnostic surface, not decoration.
 *
 * The account's language is not known here — `LocaleProvider` is answering with
 * the browser's guess, since nothing has authenticated yet — but that is still a
 * better answer than a hard-coded word, and `app.loading` exists for exactly this.
 */
function RestoringFrame({ slow }: { slow: boolean }) {
  const t = useT();
  return (
    <div
      className="h-dvh w-full flex items-center justify-center"
      style={{ background: '#0a0b10' }}
      data-testid="restoring-frame"
    >
      {slow && (
        <p style={{ color: '#e6fbff', opacity: 0.7, font: '14px/1.6 system-ui, sans-serif' }}>
          {t('app.loading')}
        </p>
      )}
    </div>
  );
}

/** How often the «no connection» frame asks again on its own (#1145). */
export const OFFLINE_RETRY_MS = 15_000;

/**
 * «WE COULD NOT ASK» — the restore could not reach the server (#1145, AUD-026).
 *
 * In place of the sign-in screen, which is what a failed restore used to show:
 * a person whose cookie is intact must not be told to sign in again against a
 * server that is not there. It asks again on its own — when the browser says
 * it is back online, and every OFFLINE_RETRY_MS — and on the button.
 */
function OfflineFrame({ onRetry }: { onRetry: () => void }) {
  const t = useT();
  useEffect(() => {
    window.addEventListener('online', onRetry);
    const timer = setInterval(onRetry, OFFLINE_RETRY_MS);
    return () => {
      window.removeEventListener('online', onRetry);
      clearInterval(timer);
    };
  }, [onRetry]);
  return (
    <div
      className="h-dvh w-full flex items-center justify-center px-6"
      style={{ background: '#0a0b10' }}
      data-testid="offline-frame"
    >
      <div role="alert" className="max-w-sm text-center" style={{ color: '#e6fbff' }}>
        <p style={{ font: '600 16px/1.5 system-ui, sans-serif' }}>{t('app.offline.title')}</p>
        <p style={{ opacity: 0.75, font: '14px/1.6 system-ui, sans-serif', marginTop: 8 }}>
          {t('app.offline.body')}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-5 rounded-lg px-4 py-2 font-semibold focus-ring"
          style={{ background: '#e6fbff', color: '#0a0b10' }}
        >
          {t('app.offline.retry')}
        </button>
      </div>
    </div>
  );
}

export default function Padel24App() {
  // THE API PATH HAS A REAL SESSION (#186), AND IT IS NOW RESTORABLE (#569).
  //
  // This used to say «there is nothing to restore on load and a reload returns
  // to the login screen. That is the point». It was true, and it was the
  // complaint: a refresh signed the operator out and a second tab never was.
  //
  // The bearer token still dies with the page — nothing readable is stored —
  // but the same session is also a `HttpOnly` cookie, so «is somebody signed
  // in» has an answer the SERVER can give. `restoring` below is the third state
  // that asking for it requires. A local flag claiming the user is signed in
  // would still be wrong, which is why this asks `/me` rather than remembering.
  //
  // The in-memory backend keeps the sessionStorage flag, because it has no
  // server session to hold instead.
  const apiAuth = Storage.backend === 'http';
  // THE MEMBER APP'S GATE (#185). Read once — the surface does not change under a
  // running page, and re-deciding mid-life would swap the app's data source.
  // The one exception is a dev-persona switch, which changes it deliberately
  // and remounts the store when it does (see `epoch` below).
  const [memberJoinNeeded, setMemberJoinNeeded] = useState(() =>
    memberJoinRequiredFor(Storage.backend as StorageBackend, memberSurfaceAtLoad()),
  );
  // ── ONE ANSWER TO «WHICH SURFACE IS THIS», FOR BOTH GATES (#847 review) ──
  //
  // The portal's login gate re-read the ADDRESS on every render, while the
  // member gate above and `AppShell` read it once. Mid-visit the address moves
  // under them — the portal writes its current view into the hash — so the
  // two gates could disagree about the page they were guarding. It reads the
  // member gate's own state now. Every deliberate surface change (a persona
  // switch, entering an organization, returning to the player app) rewrites
  // the address AND calls `setMemberJoinNeeded`, so this still notices them;
  // what it no longer does is notice the portal's own navigation.
  //
  // `memberJoinNeeded` is «the member surface, on the API». Off the API the
  // portal needs no login either way, so nothing is lost.
  const requireLogin = loginRequiredFor(Storage.backend as StorageBackend, memberJoinNeeded);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    if (!requireLogin) return true; // Claude / localStorage: no login
    if (apiAuth) return false;
    try {
      return sessionStorage.getItem('padel24_auth') === '1';
    } catch {
      return false;
    }
  });
  const [endedBecause, setEndedBecause] = useState<string | null>(null);

  // SCOPE IS SEPARATE FROM AUTHENTICATION (#186). Signing in says who this is;
  // where they may act is a second question, and «nowhere» is a valid answer
  // rather than a failed login.
  //
  // ── DECLARED BEFORE THE RESTORES THAT PUBLISH INTO IT (#788 review) ──
  //
  // This block used to sit BELOW the member entry's boot effect, and React
  // runs effects in declaration order — so the restore published its result
  // before this subscription existed, and the state it published was lost. In
  // production the restore is a network round trip and the subscription wins
  // by luck; in a test that resolves immediately it does not, which is how the
  // ordering became visible. Depending on a race is not better for being
  // usually won.
  const [session, setSession] = useState<ClubSessionState>(() => clubSession.state());

  // REMEMBER WHERE THIS ACCOUNT WORKS (decision 4A, 2026-10-19), so the next
  // sign-in lands in the same organization. A browser preference only — see
  // `lib/auth/lastScope.ts`.
  const sessionAccountId = session.account?.id ?? null;
  const sessionTenantId = session.currentScope?.tenantId ?? null;
  useEffect(() => {
    writeLastScope(sessionAccountId, sessionTenantId);
  }, [sessionAccountId, sessionTenantId]);

  // The transport drops the session when the API refuses its token; this is how
  // that reaches the screen. One subscription, so every route gets the same
  // behaviour without any of them knowing about auth.
  useEffect(() => {
    if (!apiAuth) return undefined;
    return clubSession.subscribe((state) => {
      setSession(state);
      if (state.status === 'signed-out') {
        setIsAuthenticated(false);
        setEndedBecause(state.endedBecause === 'unauthorized' ? 'unauthorized' : null);
      }
      // ── A SELECTION BELONGS TO THE ACCOUNT THAT MADE IT (#801 review) ──
      //
      // The subscription is how a 401 reaches the screen, and it did not
      // reach the entered club: clearing the selection was the only thing that cleared
      // it, so a revoked session left the gate — which asks «is a club
      // entered» — satisfied, the previous `StoreProvider` mounted, and the
      // workspace showing the last account's club and roster instead of the
      // player entrance. A later sign-in in the same tab then inherited a
      // club it never chose, which is the explicit-selection invariant
      // `selectPlayerClub` exists for.
      //
      // CLEARED ON BOTH CONDITIONS, not only on sign-out: an account REPLACED
      // in the same tab is signed-in throughout, and «the same club» is not
      // the same choice.
      setEntered((current) => {
        if (!current) return current;
        const live = state.status === 'signed-in' ? (state.account?.id ?? null) : null;
        if (live !== null && live === current.accountId) return current;
        selectPlayerClub(null);
        return null;
      });
    });
  }, [apiAuth]);

  // ── A THIRD STATE, BECAUSE ASKING TAKES A ROUND TRIP (#569) ────────
  //
  // Not «signed in» and not «signed out» but «we have not asked yet». Rendering
  // the login screen while `/me` is in flight would flash it in front of
  // somebody who IS signed in, on every refresh, and then jump them into the
  // portal — worse than the blank moment it saves.
  const [restoring, setRestoring] = useState(() => apiAuth && requireLogin);
  // ── AND WHETHER TO SAY SO (#579) ─────────────────────────────────
  //
  // The restoring frame is dark and empty, which is pixel-identical to a page
  // whose JavaScript never loaded. That cost twenty minutes on 2026-09-28: the
  // bundle 404'd after two deploys landed together, and «black screen» read as
  // «stuck restoring» when nothing of this file had run at all.
  //
  // So it says something — but NOT IMMEDIATELY. `/me` usually answers in well
  // under a second, and a spinner that appears and vanishes in 200ms is worse
  // than the blank it replaced. Silent while it is quick, explains itself when
  // it is not, which is also exactly when somebody starts wondering.
  const [restoreSlow, setRestoreSlow] = useState(false);

  useEffect(() => {
    if (!restoring) return undefined;
    const slow = setTimeout(() => setRestoreSlow(true), 600);
    return () => clearTimeout(slow);
  }, [restoring]);

  // «WE COULD NOT ASK» (#1145): a restore that could not reach the server, and
  // the attempt counter its retry advances.
  const [restoreOffline, setRestoreOffline] = useState(false);
  const [restoreAttempt, setRestoreAttempt] = useState(0);
  const retryRestore = useCallback(() => {
    setRestoreOffline(false);
    setRestoring(true);
    setRestoreAttempt((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!restoring) return undefined;
    let cancelled = false;
    clearRestoreUnreachable();
    void clubSession.restore().then((restored) => {
      if (cancelled) return;
      if (restored) setIsAuthenticated(true);
      else if (restoreUnreachable()) setRestoreOffline(true);
      setRestoring(false);
    });
    return () => {
      cancelled = true;
    };
    // Once per attempt: this answers «was there a session when the page
    // loaded», asked again only by `retryRestore`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoreAttempt]);

  // ═══════════════════════════════════════════════════════════════════
  // OPERATING A CUSTOMER'S ORGANIZATION (RBAC-3).
  //
  // A StarPoint employee who started a context in /sa arrives here with an
  // Entra session and NO club session — so every gate below would send them to
  // the club login screen, which is what «Arbeid som organisasjonen» did until
  // now: start a context, show a banner, and go nowhere.
  //
  // It is a SECOND WAY IN, not a bypass. The authority is the server's: every
  // request carries the context token beside the ordinary Authorization, and
  // `requireCapability` decides. What this does is let the portal render for
  // somebody the API will say yes to — nothing here grants anything.
  //
  // THE ORGANIZATION IS THE SCOPE. There is no scope question to ask: the
  // context names exactly one organization, chosen deliberately and written
  // into an audit row, so the selector and the «nowhere to act» screen are
  // both skipped rather than answered.
  // ═══════════════════════════════════════════════════════════════════
  const operatingAs = useImpersonation();

  // WHEN IT ENDS, GO BACK. «Avslutt», the 45-minute expiry and a refusal from
  // the API all end the context the same way, and all three leave a StarPoint
  // employee standing in a club portal they can no longer act in. Watching the
  // state rather than hooking the button is what makes the other two behave
  // like the button.
  const hadContext = useRef(false);
  useEffect(() => {
    if (operatingAs) {
      hadContext.current = true;
      return;
    }
    if (!hadContext.current) return; // never had one: an ordinary visitor
    hadContext.current = false;
    navigateToSurface('superadmin');
  }, [operatingAs]);

  // THE NATIVE SHELL, read once like every other shell fact. `AppHeader` —
  // and with it the way back out of a workspace — does not render when this
  // is true, which is why the organization door is not offered there
  // (#830 review).
  const [standaloneMobile] = useState(() => isStandaloneMobile());

  // ASKED OF THE SESSION, NOT OF THE TOKEN (#620). The token is null after every
  // page load by design; «am I signed in» is the session's own answer, and keying
  // a gate on the token is what made the whole app take the club path after a
  // reload — see `memberModeActive`.
  const [memberSignedIn, setMemberSignedIn] = useState(
    () => memberSession.state().status === 'signed-in',
  );
  /**
   * Whether the cookie has been asked about yet (#620).
   *
   * Nothing is known about the session on the first frame after a page load — the
   * token lived in memory and the cookie is unreadable from here — so showing the
   * join screen on that alone asked a signed-in member for a club code on every
   * refresh. This holds the gate shut for the one round-trip it takes to find out.
   */
  const [memberRestoring, setMemberRestoring] = useState(
    () =>
      memberJoinRequiredFor(Storage.backend as StorageBackend, memberSurfaceAtLoad()) &&
      memberSession.state().status !== 'signed-in',
  );
  /**
   * The same delayed word the portal learned to show (#579).
   *
   * `return null` while asking was the member-app copy of the bug that cost
   * twenty minutes on 2026-09-28: a blank frame says «stuck» and «broken bundle»
   * and «still loading» equally well. Silent while it is quick — a spinner that
   * appears and vanishes in 200ms is worse than the blank — and a sentence once it
   * is not, which is exactly when somebody starts wondering.
   */
  const [memberRestoreSlow, setMemberRestoreSlow] = useState(false);
  // ── REGISTERING, FOR SOMEBODY NO CLUB HAS INVITED (#710) ───────────
  //
  // `onboardingOpen` is the journey; `onboardingGrant` is where the `spo_`
  // credential it produces lives — IN MEMORY, see `lib/auth/onboarding.ts`.
  //
  // `useRef` rather than `useState` because the holder is not render state: it
  // is a box, and re-creating it on a re-render would drop a live grant
  // mid-journey. `useState(() => …)` would also work; a ref says «this is not
  // rendered» out loud.
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  // ── ONE DOOR (#710 direction correction) ───────────────────────────
  //
  // The app opens on the e-mail door and offers no other, because *«a user
  // opening the player app should never be asked which kind of user they
  // are»*. There is no club field, no club code and no identity choice.
  // ── THE PLAYER DOOR, AND WHAT IT LANDS ON (#710 decision 2) ────────
  //
  // `/auth/otp` signs somebody in WITHOUT a club, which the member gate had no
  // state for: it asked for a club code, and registration ended with «the club
  // has your request» and no session at all. Somebody who had proved who they
  // were was shown a door.
  //
  // TWO FLAGS AND NOT ONE. «The door is open» and «they are through it» are
  // different states and the second outlives the first — collapsing them would
  // reopen the sign-in screen behind the home.
  // ── THE ADDRESS, FOR «SIGNED IN AS …» BEFORE `/me` HAS ANSWERED ────
  //
  // A fallback only: the session's own `account.email` is the authority, and
  // after a restore it is the ONLY thing available, because nobody typed an
  // address this time.
  /**
   * THE CLUB A PLAYER HAS ENTERED (#800).
   *
   * Held here as well as in `customerContext` on purpose, and the two halves
   * do different work: the module's copy is what the TRANSPORT addresses, and
   * this one is what makes the shell re-render when it changes. One setter
   * writes both, so they cannot diverge.
   *
   * Initialised FROM the module so a re-mount of this component (a dev-persona
   * switch, an epoch bump) does not drop somebody out of the club they are in.
   */
  const [entered, setEntered] = useState<{ club: PlayerClub; accountId: string | null } | null>(
    () => {
      const club = selectedPlayerClub();
      return club ? { club, accountId: clubSession.state().account?.id ?? null } : null;
    },
  );
  /**
   * THE CLUB THIS ACCOUNT HAS ENTERED — derived, not merely remembered.
   *
   * #801 review: the stored selection WAS the entrance condition, and nothing
   * revisited it when the session went away. So a revoked token left the gate
   * satisfied and the previous account's workspace mounted, and the next
   * sign-in in the same tab inherited a club it never chose.
   *
   * DERIVING IT closes the timing hole as well as the logic one: the
   * subscription below clears the module state the TRANSPORT reads, and this
   * renders correctly on the frame before that arrives. Both halves are
   * needed, and the derivation is the one that cannot be late.
   */
  const enteredClub =
    entered && session.status === 'signed-in' && (session.account?.id ?? null) === entered.accountId
      ? entered.club
      : null;
  /**
   * SELECT THE ACTIVE CLUB CONTEXT — or clear it with `null`.
   *
   * ── A CONTEXT, NOT A DOOR (owner, 2026-10-16) ─────────────────────
   *
   * Called by the player app's 📍 selector, from INSIDE the app. It changes
   * which club the club-specific features address; it does not move the
   * player anywhere, and the app stays mounted — the store reloads the club's
   * data in place (`useMemberBootstrap`). Clearing it returns the player to
   * the app's own personal Hjem, not to a screen before the app.
   *
   * ORDER MATTERS: the transport's module state is set BEFORE the React state
   * is published, so the reload this triggers already carries the new club's
   * tenant header. The server still checks the roster row for that club on
   * every request — selecting grants nothing.
   */
  const selectClub = (club: PlayerClub | null) => {
    if (!club) {
      selectPlayerClub(null);
      setEntered(null);
      return;
    }
    // NORMALISED TO EXACTLY WHAT IT DECLARES. The caller hands over a
    // `MyClub`, which carries `kind` as well — and a struct holding
    // undeclared fields is how a shape drifts: the next reader cannot tell
    // which of them are load-bearing. Three fields are: the id addresses the
    // club, the name labels it, the slug identifies it in a URL.
    const club2: PlayerClub = { id: club.id, slug: club.slug, name: club.name };
    selectPlayerClub(club2);
    // WHOSE SELECTION IT IS, recorded with it (#801 review). The selection is
    // one ACCOUNT's explicit choice; without the owner, a different account in
    // the same tab inherits a club it never chose — and the entrance gate,
    // which keys on «is a club entered», would let it straight past the club
    // list into the previous person's workspace.
    setEntered({ club: club2, accountId: clubSession.state().account?.id ?? null });
  };
  const onboardingGrant = useRef(createOnboardingGrant()).current;

  // ── A CLUB'S E-MAIL INVITATION (#1069) ────────────────────────────
  //
  // The mail links to `/?invitation=<token>`. Read ONCE, at mount: the screen
  // owns the journey from there, and the token leaves the address bar as soon
  // as the journey ends (accepted or set aside) so a reload or a shared URL
  // cannot replay it.
  const [invitationToken, setInvitationToken] = useState<string | null>(() =>
    invitationTokenFromLocation(),
  );
  const leaveInvitation = () => {
    clearInvitationFromLocation();
    setInvitationToken(null);
  };
  const handleInvitationAccepted = async () => {
    leaveInvitation();
    // RESTORE, NOT ADOPT. `accept` set the `spm_` cookie; `/member/me` says who
    // that is, which is the path a reload takes (#620). If it does not answer,
    // the person lands on the entrance rather than in an app with nobody in it.
    const restored = await memberSession.restore();
    if (!restored) return;
    // THE SAME MOVE AS A MEMBER PERSONA: the transport prefers the club token,
    // so a portal session held in this tab goes first — locally only; its
    // cookie is the server's, and a reload restores both with the member
    // session winning, as `memberAuthEither` does.
    clubSession.invalidate();
    rewriteLocationForSurface('member');
    selectCustomerSurface('member');
    setMemberJoinNeeded(true);
    setMemberSignedIn(true);
    setIsAuthenticated(true);
    setEndedBecause(null);
    setEpoch((e) => e + 1);
  };
  useEffect(() => {
    if (!memberRestoring) return undefined;
    const slow = setTimeout(() => setMemberRestoreSlow(true), 600);
    return () => clearTimeout(slow);
  }, [memberRestoring]);
  // «WE COULD NOT ASK» on the member entry too (#1145).
  const [memberRestoreOffline, setMemberRestoreOffline] = useState(false);
  const [memberRestoreAttempt, setMemberRestoreAttempt] = useState(0);
  const retryMemberRestore = useCallback(() => {
    setMemberRestoreOffline(false);
    setMemberRestoring(true);
    setMemberRestoreAttempt((n) => n + 1);
  }, []);
  useEffect(() => {
    if (!memberRestoring) return undefined;
    let cancelled = false;
    clearRestoreUnreachable();
    // ── BOTH CREDENTIALS, BECAUSE EITHER CAN BE THE ONE (#710) ──────
    //
    // This asked only `memberSession.restore()`, and on this entry the
    // ACCOUNT session was never restored at all: `requireLogin` is false when
    // `standaloneMobile` is true, so the portal's own restore effect returns
    // early and nothing else asks.
    //
    // That made the player-first door a one-session wonder — sign in, and the
    // next refresh showed the sign-in screen again with a perfectly valid
    // `sps_session` cookie in the browser. It is the exact property #620
    // exists for, reintroduced one door along (#788 review).
    //
    // IN PARALLEL AND BOTH AWAITED, so the gate below decides once rather
    // than flashing one answer and replacing it.
    void Promise.all([memberSession.restore(), clubSession.restore()]).then(([member, account]) => {
      if (cancelled) return;
      if (member) setMemberSignedIn(true);
      // NEITHER restored, and at least one could not reach the server: that is
      // «we could not ask», not «nobody is here».
      if (!member && !account && restoreUnreachable()) setMemberRestoreOffline(true);
      // THE LEGACY ACTOR WINS WHERE THERE IS ONE. A browser holding both
      // cookies restores the `spm_` session's own identity and scope — the
      // same precedence the server applies (`memberAuthEither`) and the
      // client's own `credentialFor`. The surface is selected explicitly
      // ONLY for the account-only case, which is the new state: without it
      // `memberModeActive()` is false, the context would answer `club`, and
      // a player's requests would address an administrator's selection.
      if (!member && account) {
        selectCustomerSurface('member');
        // ── BACK FROM VIPPS, BACK IN THE CLUB (#508) ─────────────────
        //
        // The session survived the trip as a cookie; the entered club did not
        // — it is in memory by design (#800), so without this a member
        // returning from paying lands on their own Hjem, and the «did it
        // work?» read would go out naming no club at all. The return URL
        // carried the club they were paying in (`lib/paymentReturn.ts`). It is
        // re-entered ONCE, here, by the account that just restored, and grants
        // nothing: the server checks the roster row for the club a request
        // names. A reload carries no return, so it lands on Hjem as before.
        const back = peekPaymentReturn();
        if (back?.club) selectClub(back.club);
      }
      setMemberRestoring(false);
    });
    return () => {
      cancelled = true;
    };
    // Once per attempt: «was there a member session when the page loaded»,
    // asked again only by `retryMemberRestore`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberRestoreAttempt]);
  useEffect(() => {
    if (!memberJoinNeeded) return undefined;
    // The transport ends the session on a 401; this is how that reaches the
    // screen, exactly as the portal's gate does it.
    return memberSession.subscribe((st) => setMemberSignedIn(st.status === 'signed-in'));
  }, [memberJoinNeeded]);
  // THE UI LANGUAGE FOLLOWS THE ACCOUNT WHILE ONE IS SIGNED IN (#310).
  //
  // Mounted HERE — in the gate, above the login screen as well as the portal
  // — because both sides of the question live here. Signed out, the browser's
  // own preference governs and the login screen switches with no session at
  // all; signed in, the account's stored choice wins and a change is written
  // to it. `clubSession` is the only session with a `User` row behind it: a
  // MEMBER has no account to store a preference on, so the member surface
  // keeps the browser's (see i18n/accountLocale.ts).
  //
  // The session state, not `isAuthenticated`: the latter is also true for a
  // StarPoint employee operating an organization under a context, who has no
  // club session and whose own account is not this portal's to write to.
  useAccountLocale(session.status === 'signed-in');

  // DEV PERSONAS. `persona` is which one is active (for the bar); `epoch`
  // increments on every switch and keys the store, so a switch from one
  // organization to another discards every hook's state instead of showing
  // the previous organization's players under the new one's header (#399).
  const [persona, setPersona] = useState<string | null>(null);
  const [epoch, setEpoch] = useState(0);

  const markAuthed = () => {
    setIsAuthenticated(true);
    setEndedBecause(null);
    // Sign-in resolved the scopes before returning, so read them now rather
    // than waiting for a notification that has already been delivered.
    if (apiAuth) setSession(clubSession.state());
    if (apiAuth) return; // the session itself is the authority here
    try {
      sessionStorage.setItem('padel24_auth', '1');
    } catch {
      /* ignore */
    }
  };

  // ── THE TWO SURFACE TRANSITIONS (#752, and its review) ────────────
  //
  // `selectCustomerSurface` and `selectPlayerClub` are MODULE-LEVEL setters
  // with no subscription: nothing re-renders when they are called. The first
  // version of this called them and nothing else, which did the worst
  // possible thing — the TRANSPORT started addressing the member surface
  // while the administrative portal stayed on screen, because
  // `memberJoinNeeded` is React state and kept its page-load value.
  //
  // So each transition sets the module state AND the React state that decides
  // which branch renders. `memberJoinNeeded` is the switch: true renders the
  // personal surface, false mounts `AppShell`. It is the same lever a dev
  // persona switch already pulls, for the same reason.
  const enterOrganization = (tenantId: string) => {
    // ── MOUNTING THE SHELL IS NOT ARRIVING IN A WORKSPACE (#830 review) ──
    //
    // The first version set the surface, the scope and the branch, and that
    // was still wrong on the URL this route actually starts from.
    // `AppShell` reads `initialViewFromLocation()` on ITS first mount, and
    // `#mobile` — the player entrance — answers `'mobile'`. So an
    // administrator entering their organization got the MEMBER app with a
    // club transport context behind it. Mount ≠ workspace.
    //
    // The address is what decides, so the address is what changes — the same
    // thing `rewriteLocationForPersona` does for a persona switch, and for
    // the same reason: a reload must land where the screen already is,
    // rather than undoing the transition.
    rewriteLocationForSurface('club');
    selectCustomerSurface('club');
    // The scope the store is keyed on (#399), so an organization change
    // remounts every organization-keyed view. Set BEFORE the branch flips so
    // the shell mounts with the right one rather than remounting again.
    clubSession.selectScope(tenantId);
    setMemberJoinNeeded(false);
  };

  const goToPersonalHome = () => {
    // ── THE SCOPE IS LEFT ALONE, AND THAT IS DELIBERATE ─────────────
    //
    // The first version called `clubSession.selectScope(null)` to clear it.
    // That is a SILENT NO-OP: `selectScope` looks the id up in the scope list
    // and ignores anything it does not find, so null never clears anything —
    // a line that reads as a guarantee and does nothing.
    //
    // It is not needed either. The review's worry was that «enter the
    // organization I just left» would be suppressed by the switcher's
    // `tenantId === currentTenantId` check, making the route back one-way.
    // The way back in is the PERSONAL HOME's own list, which has no such
    // check — the switcher lives inside `AppShell` and is not even mounted
    // here. A scope left selected is inert on this surface: `customerContext`
    // reads `playerClub` for a member, never `currentScope`.
    rewriteLocationForSurface('member');
    // BOTH halves of the selection, not only the transport's: a module-state
    // clear under a React selection that survived would show club A's name
    // over requests that address no club.
    selectClub(null);
    selectCustomerSurface('member');
    setMemberJoinNeeded(true);
  };

  // ── WHERE A FRESH SIGN-IN LANDS (owner decision 4A, 2026-10-19) ──────
  //
  // #751 sent EVERYBODY to «Min StarPoint» first, administrators included,
  // to choose to administer afterwards. The owner reversed that for anybody
  // who has somewhere to work: an account with an organization role lands in
  // the portal — in the organization it used last on this browser, or in the
  // picker when it has several and none is remembered. An account with no
  // organization still lands on the personal home, which is the half of #751
  // that was right: sending it to the portal would meet «Ingen tilgang».
  // Both credentials (code and passkey) go through here, so the destination
  // never depends on which one somebody used (#837 re-review).
  const landAfterSignIn = () => {
    const state = clubSession.state();
    const landing = signInLanding(state.scopes, readLastScope(state.account?.id));
    if (landing.surface === 'personal') {
      goToPersonalHome();
      return;
    }
    if (landing.preselect) clubSession.selectScope(landing.preselect);
    rewriteLocationForSurface('club');
    selectCustomerSurface('club');
    setMemberJoinNeeded(false);
    setSession(clubSession.state());
  };

  // ── ONE PASSKEY SIGN-IN, TWO LANDINGS ──────────────────────────────
  //
  // The ceremony, the adoption and the failure codes are the same at both
  // entrances — the club portal's and the player app's — and only where the
  // person lands afterwards differs, the same way it differs for the e-mail
  // code. So it is written once and handed the landing: a second copy of the
  // throttle arithmetic or the code mapping is a second place for them to
  // drift.
  //
  // THE GATE ADOPTS THE SESSION, THE SCREEN SHOWS THE MESSAGE. `clubSession`
  // is the one place that decides what «signed in» means, so a second
  // establisher is a second thing that can get the token, the expiry or the
  // account wrong. The screen gets back a FAILURE CODE and nothing else — it
  // owns the wording, and this component has no catalog to write it with.
  //
  // `passkeysSupported()` gates the offer, so a browser without WebAuthn
  // renders no button rather than one that cannot work.
  const passkeyOffered = apiAuth && passkeysSupported();
  // `remember` (#1091) is the player screen's «Husk meg» or the native app;
  // the portal calls this with no argument and keeps twelve hours.
  const passkeySignIn =
    (land: () => void) =>
    async (remember = false): Promise<PasskeySignInFailure | null> => {
      const outcome = await signInWithPasskey(remember);
      if (outcome.status === 'signed-in') {
        await clubSession.adopt(outcome);
        land();
        return null;
      }
      if (outcome.status === 'throttled') {
        // THE SECONDS, AS THE SERVER SAID (#778 Q7). The screen owns the
        // rounding and the countdown (`i18n/retryWait.ts`); the gate only
        // passes the number on.
        return { code: 'throttled', retryAfterSeconds: outcome.retryAfterSeconds };
      }
      // A CODE, never a sentence: this component has no catalog, and the
      // screen does. See `LoginScreen`'s own note on `notice`.
      return { code: outcome.status };
    };

  const handleLogout = () => {
    // UNDER A CONTEXT, «log out» means END THE CONTEXT — the person is a
    // StarPoint employee whose own Entra session is not the portal's to
    // revoke, and signing them out of the club they are visiting is not a
    // thing that exists. They go back to the panel they came from.
    if (operatingAs) {
      void endImpersonation();
      return;
    }
    setIsAuthenticated(false);
    setPersona(null);
    // #801 review: belt as well as braces. The subscription above clears this
    // when the session publishes `signed-out`, and a logout that never
    // reaches the server (the non-API backend) publishes nothing.
    selectClub(null);
    // EVERY CUSTOMER SESSION, not the one this screen happens to know
    // about (#752). `memberSession.signOut()` had no caller anywhere, so a
    // join-code member's logout cleared the screen and left the session
    // alive to be restored on the next reload.
    if (apiAuth) void signOutCustomerSessions();
    try {
      sessionStorage.removeItem('padel24_auth');
    } catch {
      /* ignore */
    }
  };

  /** A persona's REAL session arrived; become that person. */
  const handlePersona = async (p: DevPersona, res: DevPersonaResult) => {
    if (res.state !== 'ok') return;
    if (res.session.kind === 'member') {
      // The transport prefers the club token, so it must go first — locally;
      // the server's copy of a dev session is the server's to expire.
      clubSession.invalidate();
      memberSession.adopt({
        token: res.session.token,
        expiresAt: res.session.expiresAt,
        assurance: res.session.assurance,
        member: res.session.member,
        club: res.session.club,
      });
      rewriteLocationForPersona('member', res.session.club.slug);
      setMemberJoinNeeded(true);
      setMemberSignedIn(true);
      setIsAuthenticated(true);
      setEndedBecause(null);
    } else {
      memberSession.invalidate();
      rewriteLocationForPersona('club');
      setMemberJoinNeeded(false);
      setMemberSignedIn(false);
      await clubSession.adopt({
        token: res.session.token,
        expiresAt: res.session.expiresAt,
        account: res.session.account,
      });
      markAuthed();
    }
    setPersona(p.key);
    setEpoch((e) => e + 1);
  };

  // Renders nothing unless the API offers personas — dev only. And only for a
  // SIGNED-IN staff session: the listing sits behind the same session gate as
  // the switch (app.ts), so a signed-out visitor could only ever get a 401 —
  // one more expected error in every anonymous console (#1145, AUD-031).
  const personaBar =
    apiAuth && session.status === 'signed-in' ? (
      <DevPersonaBar current={persona} onSession={handlePersona} />
    ) : null;

  // NOT WHILE THE COOKIE IS STILL BEING ASKED ABOUT. Rendering the code screen
  // first and replacing it a moment later is the same bug with a flash: the member
  // sees «skriv inn klubbkode» when they were never signed out (#620).
  // ── THE INVITATION, BEFORE ANY OTHER DOOR (#1069) ──────────────────
  //
  // Whoever opened the link is answering one club's invitation, signed in or
  // not: the code it mails is what proves them, so no session is asked for
  // first. Only on the API backend — the memory backend has no invitations.
  if (invitationToken && apiAuth) {
    return (
      <>
        <MemberInvitationScreen
          token={invitationToken}
          onAccepted={handleInvitationAccepted}
          onDismiss={leaveInvitation}
        />
        {personaBar}
      </>
    );
  }

  if (memberJoinNeeded && memberRestoring) return <RestoringFrame slow={memberRestoreSlow} />;
  // NOT the sign-in screen when the restore could not reach the server (#1145).
  if (memberJoinNeeded && memberRestoreOffline && !memberSignedIn) {
    return <OfflineFrame onRetry={retryMemberRestore} />;
  }

  // A member with no session sees the code screen and nothing else — the app
  // behind it has no data source without one.
  // ── AND NOT WHILE A PLAYER IS INSIDE A CLUB (#800) ───────────────
  //
  // The branch below is the player's ENTRANCE — sign in, their club list, the
  // public directory. Entering a club is what takes them past it: the app then
  // falls through to the shell, which renders the member workspace for this
  // surface, and `customerContext` addresses the club they chose.
  //
  // ── THE ENTRANCE IS FOR SOMEBODY NOT SIGNED IN, AND NOTHING ELSE ──
  //
  // StarPoint is a player-first application, not a club application a player
  // must enter (owner, 2026-10-16). So once ANY session answers — a legacy
  // member session or an account — the player is in the app, with zero, one
  // or several clubs. There is no screen between signing in and the app: the
  // account's personal home is the app's own Hjem, and which club is active is
  // a CONTEXT chosen inside it with the 📍 selector, never a door in front of
  // it.
  //
  // This used to read `!enteredClub`, which made a selected club the condition
  // for reaching the app, and a placeholder home (`MinStarpoint`) stood in
  // front of it for everybody else. Both are gone.
  if (memberJoinNeeded && !memberSignedIn && session.status !== 'signed-in') {
    // REGISTERING IS ITS OWN SCREEN: it has four steps and its own
    // credential.
    //
    // IT DOES NOT SIGN ANYBODY IN, and cannot: it finishes with a request a
    // club has to approve, so there is no session to establish and the gate
    // below is untouched by it. A sign-in ends with `memberSignedIn`; this
    // ends with «the club has your request».
    if (onboardingOpen && apiAuth) {
      return (
        <>
          <MemberOnboardingScreen
            clubSlug={clubSlugFromLocation()}
            grant={onboardingGrant}
            onBack={() => setOnboardingOpen(false)}
          />
          {personaBar}
        </>
      );
    }
    // ── THE CLUB-CODE SCREEN IS GONE (#710 correction, #750) ────────
    //
    // It was reachable here from two buttons, both of which were competing
    // AUTHENTICATION CHOICES put to a player — the thing the direction
    // correction removes. It stopped being rendered then, and `MemberJoinScreen`
    // and its `/auth/member/start` door are now retired, as `MemberSignInScreen`
    // and its `/auth/member/otp` door were before them (#750).
    //
    // The `!apiAuth` half of the old condition was DEAD, which is worth
    // recording because it read as a fallback: `memberJoinNeeded` already
    // requires `backend === 'http'`, and `apiAuth` is exactly that — so the
    // non-API case never reached this block at all. On the memory backend the
    // member gate does not apply and the app simply loads.

    // ── THE ENTRANCE, FOR EVERYBODY (#710 direction correction) ─────
    //
    // *«A user opening the player app should never be asked which kind of user
    // they are.»* So this is the screen, not one of three choices behind a
    // «log in as a player» button — and it asks for an address and nothing
    // else.
    //
    // `MemberSignInScreen` used to be reached from here, and drove the legacy
    // `/auth/member/otp`. Both are retired (#750): this door is the one for
    // the same proof. The club-code screen is not rendered either — on the
    // memory backend the member gate does not apply, and dev personas adopt a
    // session without it.
    {
      return (
        <>
          <PlayerSignInScreen
            onSignedIn={async () => {
              // ── RESTORE RATHER THAN ADOPT, DELIBERATELY ───────────
              //
              // The door answers with a token and an expiry and NO account —
              // there is nothing to build a session's identity from. The
              // cookie is already set, so `restore()` asks `/me` and
              // establishes the account AND the scope discovery from the
              // server, which is the same path a reload takes. Adopting a
              // session whose account we had invented would be the quiet
              // degradation #620 is about.
              const restored = await clubSession.restore();
              if (!restored) return;
              // THE MEMBER SURFACE, EXPLICITLY. This person is a player, not an
              // administrator — `customerContext` keys the credential and the
              // tenant off the surface, and leaving it unset would make an
              // account with no clubs look like a club session with no scope.
              selectCustomerSurface('member');
            }}
            // ── THE PASSKEY, ON THE PLAYER'S DOOR TOO ───────────────────
            //
            // The member app's WebView can run the ceremony (#676) and the
            // server accepts the app's origin (#968). Same ceremony, same
            // adoption and same failure codes as the portal — `passkeySignIn`
            // — and the PLAYER's landing, the one the code above takes: the
            // member surface, explicitly, never the portal's
            // `landAfterSignIn`. Whoever opened the player app is a player
            // here, whatever organizations the account also administers.
            onSignInWithPasskey={
              passkeyOffered ? passkeySignIn(() => selectCustomerSurface('member')) : undefined
            }
          />
          {personaBar}
        </>
      );
    }
  }

  // ── NO RESET LANDING EITHER (#754) ────────────────────────────────
  //
  // `ResetPasswordScreen` consumed a token from the address bar and set a new
  // password. Every sender of those links is gone — self-service recovery, the
  // platform recovery action, and the organization invitation, which now names
  // the sign-in page instead — and so is the route it posted to. A screen
  // nothing can reach, wired to an endpoint that answers 404, is worse than
  // no screen: it looks like a working journey in the file.
  // ── «GLEMT PASSORD» IS GONE WITH THE PASSWORD (#754) ──────────────
  //
  // `ForgotPasswordScreen` asked `/auth/club/recovery/request` to mail a link
  // that set a new password. There is no password to forget: no screen
  // collects one and no route accepts one.
  //
  // THE RESET LANDING BELOW STAYS, and that is not an oversight. A platform
  // administrator still provisions a new club user by mailing a reset link
  // (`platformUsers`, `platformOrganizations`), and that link lands on
  // `ResetPasswordScreen`. Removing it would break onboarding for every new
  // club administrator — a different journey, on the SA surface, with its own
  // decision to take. See the PR.
  // ── NO CLUB CODE SCREEN EITHER (#845) ─────────────────────────────
  //
  // `CodeSignInScreen` asked `/auth/club/otp`, and #837 moved the entrance to
  // the ACCOUNT door (`/auth/otp`), which `LoginScreen` carries inline through
  // `AccountCodeSignIn`. For an address that already has an account the two
  // doors resolve the same LOCAL identity and issue the same `VERIFIED` session;
  // the account door also serves the address that has none. Nothing opened the
  // old screen after that, so it was deleted rather than kept as a journey that
  // only its tests could reach.

  // If login is required and the user is not logged in, show only the login
  // screen. A live organization context is the other way in — see above.
  // Nothing, briefly, rather than the wrong thing — then a word, rather than a
  // blank that could mean anything (#579). Matches the dark ground the shell
  // paints so a restored session does not flash white on the way in.
  if (restoring) {
    return <RestoringFrame slow={restoreSlow} />;
  }
  // NOT the sign-in screen when the restore could not reach the server (#1145).
  if (requireLogin && restoreOffline && !isAuthenticated && !operatingAs) {
    return <OfflineFrame onRetry={retryRestore} />;
  }

  if (requireLogin && !isAuthenticated && !operatingAs) {
    return (
      <>
        <LoginScreen
          notice={endedBecause === 'unauthorized' ? 'session_expired' : undefined}
          // ── THE ACCOUNT DOOR, NOT THE CLUB ONE (#837 review) ────
          //
          // This opened `CodeSignInScreen` (deleted, #845), which asked
          // `/auth/club/otp` — and that route issues a code ONLY for an
          // existing active local identity. A new player at the product's
          // front door got the non-enumerating 202 and no code: a dead step,
          // for exactly the population #751 exists to serve.
          //
          // The screen now carries the flow inline against `/auth/otp`, the
          // door that provisions for an unknown address, and hands the
          // session up here. Same restore-rather-than-adopt reasoning as the
          // player entrance: the door answers with a token and no account, so
          // `restore()` asks `/me` and establishes the account AND the scope
          // discovery from the server — the path a reload takes.
          onSignedInWithCode={
            apiAuth
              ? async (_session) => {
                  const restored = await clubSession.restore();
                  if (!restored) return;
                  markAuthed();
                  // ── WHERE THE SIGN-IN LANDS (decision 4A, 2026-10-19) ──
                  //
                  // Without this the gate's scope branches decide the
                  // destination, and for a brand-new account they decide
                  // wrongly: `scopeStateFrom` answers `unavailable` for an
                  // empty scope list, so somebody with no club met «Ingen
                  // tilgang enda» (#837 re-review). `landAfterSignIn` sends
                  // that account to «Min StarPoint», and an account with an
                  // organization role to the portal — the owner's reversal of
                  // #751's «everybody arrives as a player». It moves the
                  // address too, so a refresh lands in the same place.
                  landAfterSignIn();
                }
              : undefined
          }
          // The ceremony and the failure codes are `passkeySignIn`'s; this
          // entrance supplies the portal's landing.
          onSignInWithPasskey={
            passkeyOffered
              ? passkeySignIn(() => {
                  markAuthed();
                  // ── THE SAME LANDING AS THE CODE (#837 re-review) ──
                  //
                  // The landing was once wired into the code callback and
                  // not this one, which made the destination depend on
                  // WHICH credential somebody used: a passkey holder with
                  // no organization met «Ingen tilgang enda».
                  //
                  // Two mechanisms, one identity, one landing (decision 4A,
                  // 2026-10-19): an account with an organization role lands
                  // in the portal — a single-scope one straight into its
                  // organization, a multi-scope one into the one it used
                  // last, or the picker when none is remembered — and an
                  // account with none on «Min StarPoint». The portal and
                  // the picker are intended destinations, not the defect
                  // this once fixed. Keeping the passkey visually secondary
                  // does not make it a secondary kind of person. It changes
                  // nothing about authority: the scopes are the server's
                  // answer either way.
                  landAfterSignIn();
                })
              : undefined
          }
        />
        {personaBar}
      </>
    );
  }

  // Authenticated, and the scope question still open. The portal is not
  // mounted yet: every request it makes needs a scope, and mounting it first
  // would fire a screenful of them with no organization to send.
  if (apiAuth && !memberJoinNeeded && !operatingAs && session.scopeStatus === 'choosing') {
    return (
      <>
        <ScopeSelector
          scopes={session.scopes}
          onSelect={(tenantId) => clubSession.selectScope(tenantId)}
          onSignOut={handleLogout}
        />
        {personaBar}
      </>
    );
  }
  // «Nowhere to act» and «we could not find out» are different facts with
  // different remedies — an administrator, or a retry.
  if (
    apiAuth &&
    !memberJoinNeeded &&
    !operatingAs &&
    (session.scopeStatus === 'unavailable' || session.scopeStatus === 'undetermined')
  ) {
    return (
      <>
        <NoScopeScreen
          email={session.account?.email ?? null}
          reason={session.scopeStatus}
          onRetry={() => void clubSession.refreshScopes()}
          onSignOut={handleLogout}
        />
        {personaBar}
      </>
    );
  }

  return (
    <>
      {/* ABOVE EVERYTHING, exactly as in /sa. The expensive mistake is not
          starting a context — it is forgetting you are in one and acting on
          the wrong club's data, and that mistake is far easier to make HERE,
          in a portal that otherwise looks like any administrator's own. */}
      {/* THE ORDER IS THE URGENCY (#537). «The server is not answering»
          explains why the screen has stopped responding and must be read
          first. «Whose data is this» comes next. «You are one build behind»
          is true but not urgent, and goes last — it is the only one of the
          three that is never about something being wrong. «A list could
          not be read» (#1247) follows the server: it is usually the same
          outage, seen in what the screen is now missing. */}
      <ServerUnreachableBanner />
      <ListLoadFailedBanner />
      <ImpersonationBanner />
      <NewBuildBanner />
      {/* BELOW the impersonation banner, deliberately. If both are somehow
          showing, the one about a real customer's data is the one to read
          first. */}
      <DevAllRightsMarker persona={persona} />
      {/* KEYED TO EVERY THING THAT CHANGES WHICH CLUB THIS IS (#399).
          Persona, organization context, and the club session's own selected
          scope. All three answer the same question — which organization are
          the screens about — and a store that survived any of them would show
          one club's players under another club's header.

          THE SCOPE IS HERE FOR A SWITCHER THAT DOES NOT EXIST YET, and that
          is the point. `selectScope` sets client state and nothing else;
          today it is only reachable from `ScopeSelector`, which renders
          BEFORE the portal mounts, so a multi-club administrator signs out to
          change club and the store is built fresh either way. The day
          somebody adds a «bytt klubb» control, the server would correctly
          serve club B while every hook still held club A's players,
          bookings, activities and archive — a portal that looks right and is
          showing another club's data, with writes composed from the wrong
          list. Remounting on the key discards all of it at once; resetting
          each hook is the same fix with one of them forgotten. */}
      <StoreProvider
        // ── THE SURFACE AND THE ACCOUNT ARE PART OF THE KEY; THE CLUB IS NOT ──
        //
        // THE SURFACE (owner, 2026-10-16). Moving between the portal and the
        // player app used to cross an early return — the placeholder home —
        // so this subtree unmounted on its own. That screen is gone and the
        // player app IS the shell now, so nothing unmounts any more, and
        // everything read once on mount would carry over: `AppShell`'s
        // initial view, the bootstrap's identity mode, the portal's `/kv`
        // loader. Keyed on the surface, one transition rebuilds all of them.
        //
        // THE ACCOUNT, for the same reason one step further. The app reads
        // who you are (`/me/player`) and where you play (`/me/clubs`) keyed by
        // nothing that names the account — it never had to, while an account
        // change crossed the placeholder home and unmounted everything. Now a
        // second account signing in over the first in the same tab would be
        // greeted with the first one's name, rating and clubs. Keyed here, an
        // account change is a new app; a session republishing ITSELF (a scope
        // refresh, a rotation) keeps the same id and rebuilds nothing.
        //
        // THE ACTIVE CLUB, DELIBERATELY NOT (#800 had it here). Changing the
        // club context happens INSIDE the player app with the 📍 selector, and
        // the app must survive it: the store reloads that club's data in
        // place (`activeClubId` → `useMemberBootstrap`, which empties every
        // club slice before the next club's answer and drops a late one). A
        // key here would tear the app down on every switch instead.
        key={`${epoch}:${operatingAs?.impersonationId ?? ''}:${
          session.currentScope?.tenantId ?? ''
        }:${memberJoinNeeded ? 'player' : 'portal'}:${session.account?.id ?? ''}`}
        activeClubId={enteredClub?.id ?? null}
        Padel24Engine={Padel24Engine}
      >
        <AppShell
          onLogout={handleLogout}
          onEnterOrganization={enterOrganization}
          onPersonalHome={goToPersonalHome}
          // THE WAY BACK OUT (#800). Null unless a player is inside a club:
          // a member session's club is the session's and cannot be left
          // without signing out, and an administrator has a scope switcher
          // rather than this.
          // ── THE PLAYER APP'S CLUB CONTEXT (owner, 2026-10-16) ─────
          //
          // Four separate concepts reach the app separately: the ACCOUNT is the
          // session; MEMBERSHIPS the app reads itself (`/me/clubs`); the
          // ACTIVE CLUB is this; ORGANIZATION ADMINISTRATION is the scopes
          // below. `onSelectClub` is null for a legacy member session, whose
          // club is the session's own and cannot change without signing out.
          activeClub={enteredClub}
          //
          // ONLY ON THE PLAYER SURFACE, FOR AN ACCOUNT. The portal's preview
          // of the app shows the administrator's own club and has no context
          // to choose, and a member session's club is fixed.
          onSelectClub={memberJoinNeeded && !memberSignedIn ? selectClub : null}
          // THE ORGANIZATION DOOR, inside the app now that the placeholder
          // home it lived on is gone. Withheld natively, as before: the native
          // app has no way back from the portal.
          organizations={standaloneMobile ? [] : session.scopes}
          requireLogin={requireLogin}
          // SIGNED IN, THE SWITCHER MOVES INTO THE DEV MENU. It used to float
          // over the product on every page; inside the app there is a button
          // it belongs on, and one piece of development furniture is enough.
          // The bar still renders on its own on the screens BEFORE this one —
          // switching persona is how you get a session in the first place, so
          // it cannot live behind a menu that needs one.
          devPersona={
            apiAuth
              ? {
                  current: persona,
                  onSession: handlePersona,
                  // Who is signed in and where — the DEV menu says it in words.
                  account: clubSession.state().account,
                  scope: clubSession.state().currentScope,
                }
              : undefined
          }
        />
        {/* «DID IT WORK?» after coming back from Vipps (#508). Inside the store,
            so its one read goes out with the club context the return
            re-entered. Renders nothing on an ordinary load. */}
        {memberJoinNeeded && <PaymentReturnNotice />}
      </StoreProvider>
    </>
  );
}
